import arrowDownRight from '@/assets/icons/arrow-down-right.svg?url'
import arrowDownRightActive from '@/assets/icons/arrow-down-right-active.svg?url'
import { motion } from 'motion/react'
import { useRouterState } from '@tanstack/react-router'
import { localeFromPathname, localizePath } from '@/common/localization/localePath'

interface ImageCardProps {
  image1x: string
  image2x: string
  title: string
  description: string
  href: string
}

export const ImageCard = ({
  image1x,
  image2x,
  title,
  description,
  href,
}: ImageCardProps) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const locale = localeFromPathname(pathname)
  const normalizedHref = href.startsWith('/') ? href : `/${href}`
  const localizedHref = localizePath(normalizedHref, locale)

  return (
    <motion.a
      href={localizedHref}
      aria-label={title}
      className="
        group/ImageCard block cursor-pointer
        border-2 border-[#FDFFFF]
        p-4
        h-[336px] desktop:h-[377px] 
        max-w-[400px] tablet:max-w-[289px] desktop:max-w-[358px] 
        focus:outline-none
        focus:ring-2 focus:ring-[#FF6600]
      "
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <article className="flex h-full flex-col justify-between">
        {/* картинка фіксованої висоти — без змін при hover */}
        <div className="relative w-full overflow-hidden">
          <img
            src={image1x}
            srcSet={`${image1x} 1x, ${image2x} 2x`}
            sizes="(min-width: 768px) 326px, 100vw"
            alt={title}
            className="h-[212px] desktop:h-[199px] w-full"
            decoding="async"
          />
        </div>

        {/* низ: тайтл + опис + стрілки */}
        <div className="desktop:pt-[17px] flex gap-3 items-end">
          <div className="flex flex-col gap-3">
            <h3
              className="
                text-[#FDFFFF] text-left font-[Namu] font-bold text-[24px] tablet:text-[26px] desktop:text-[32px] uppercase leading-tight
                transition-all duration-500 ease-out
                group-hover/ImageCard:text-[24px] group-focus/ImageCard:text-[24px]
              "
            >
              {title}
            </h3>

            <p
              className="
                font-normal text-[#FDFFFF] text-[16px] first-letter:uppercase 
                max-h-24 overflow-hidden opacity-100 translate-y-0
                transition-all duration-500 ease-out
                 tablet:max-h-0 tablet:opacity-0 tablet:translate-y-2
    tablet:group-hover/ImageCard:max-h-24 
    tablet:group-hover/ImageCard:opacity-100 
    tablet:group-hover/ImageCard:translate-y-0
    tablet:group-focus/ImageCard:max-h-24 
    tablet:group-focus/ImageCard:opacity-100 
    tablet:group-focus/ImageCard:translate-y-0
              "
            >
              {description}
            </p>
          </div>

          {/* базова стрілка */}
          <img
            src={arrowDownRight}
            width={32}
            decoding="async"
            height={32}
            className="
              block
              group-hover/ImageCard:hidden
              group-focus/ImageCard:hidden
              max-w-[32px] max-h-[32px]
            "
            alt=""
          />

          {/* активна стрілка з легким зміщенням на hover */}
          <motion.img
            src={arrowDownRightActive}
            width={32}
            height={32}
            decoding="async"
            className="
              hidden
              group-hover/ImageCard:block
              group-focus/ImageCard:block
              max-w-[32px] max-h-[32px]
            "
            alt=""
            whileHover={{ x: 4, y: -4 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
        </div>
      </article>
    </motion.a>
  )
}
